import axios, { AxiosResponse } from 'axios';

import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { DEFAULT_API_ERROR_MESSAGE } from './constants.api';
import { PaginatedResponse } from './types.api';
import { v4 as uuidv4 } from 'uuid';
import { clearAuthData } from '@lucid/auth';

/**
 * Handles 401 Unauthorized responses by logging out the user and redirecting to login
 * with a returnTo parameter to bring them back after re-authentication
 */
const handle401Unauthorized = (): void => {
  // Clear all auth data from localStorage
  clearAuthData();

  // Clear user identity from Zustand store
  const storedData = localStorage.getItem('theme-storage');
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      if (parsedData.state) {
        parsedData.state.userIdentity = null;
        localStorage.setItem('theme-storage', JSON.stringify(parsedData));
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Get current URL to return to after login
  const returnTo = window.location.pathname + window.location.search;
  const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}&sessionExpired=true`;

  // Redirect to login page
  window.location.href = loginUrl;
};

type ApiConfig<ResponseShape> = {
  extractData?: (response: ResponseShape) => unknown;
  extractError?: (data: unknown) => string | null;
  paginate?: (response: ResponseShape) => PaginatedResponse;
  axiosConfig?: AxiosRequestConfig;
};

export function createApi<ResponseShape = unknown>(
  baseURL: string,
  config?: ApiConfig<ResponseShape>
) {
  const { extractData, extractError, paginate, axiosConfig } = config ?? {};

  const instance: AxiosInstance = axios.create({
    baseURL,
    ...axiosConfig,
  });

  instance.interceptors.request.use((config) => {
    config.headers.set('X-Correlation-Id', uuidv4());
    return config;
  });

  // Response interceptor to handle 401 Unauthorized errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        handle401Unauthorized();
        // Return a rejected promise that won't be processed further
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
      return Promise.reject(error);
    }
  );

  async function apiCall<T>(
    axiosCall: (axiosConfig?: AxiosRequestConfig) => Promise<{ data: ResponseShape }>,
    options?: {
      overrides?: Partial<ApiConfig<ResponseShape>>;
      shouldPaginate?: boolean;
    }
  ): Promise<T> {
    try {
      const axiosConfig = options?.overrides?.axiosConfig;
      const response = await axiosCall(axiosConfig);
      const { overrides, shouldPaginate = false } = options || {};
      const dataExtractor =
        overrides?.extractData || extractData || ((response: ResponseShape) => response);

      if (shouldPaginate) {
        const paginateExtractor = overrides?.paginate || paginate;
        if (!paginateExtractor)
          throw new Error('Pagination requested but no paginate function provided');
        return paginateExtractor(response.data) as T;
      }

      return dataExtractor(response.data) as T;
    } catch (error) {
      throw handleError(error, options?.overrides);
    }
  }

  function handleError(error: unknown, overrides?: Partial<ApiConfig<ResponseShape>>): string {
    if (axios.isAxiosError(error)) {
      console.error('HTTP Error:', error);

      const errorExtractor = overrides?.extractError || extractError;

      if (errorExtractor) {
        const applicationError = errorExtractor(error.response?.data);
        if (applicationError) return applicationError;
      }

      const responseData = error.response?.data as { error?: string } | undefined;
      return responseData?.error || error.message || DEFAULT_API_ERROR_MESSAGE;
    }

    return error instanceof Error ? error.message : DEFAULT_API_ERROR_MESSAGE;
  }

  return {
    instance,
    async get<TResponse>(url: string, overrides?: ApiConfig<ResponseShape>): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.get<ResponseShape>(url, axiosConfig), {
        overrides,
      });
    },

    async getPaginated<TResponse>(
      url: string,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<PaginatedResponse<TResponse>> {
      return apiCall((axiosConfig) => instance.get<ResponseShape>(url, axiosConfig), {
        overrides,
        shouldPaginate: true,
      });
    },

    async post<TRequest, TResponse>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.post<ResponseShape>(url, data, axiosConfig), {
        overrides,
      });
    },

    async put<TRequest, TResponse>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.put<ResponseShape>(url, data, axiosConfig), {
        overrides,
      });
    },

    async patch<TRequest, TResponse>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.patch<ResponseShape>(url, data, axiosConfig), {
        overrides,
      });
    },

    async delete<TRequest = void, TResponse = void>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall(
        (axiosConfig) => instance.delete<ResponseShape>(url, { data, ...axiosConfig }),
        { overrides }
      );
    },

    async downloadFile<TRequest>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<AxiosResponse<Blob>> {
      try {
        const axiosConfig = overrides?.axiosConfig || {};
        return await instance.post<Blob>(url, data, { ...axiosConfig, responseType: 'blob' });
      } catch (error) {
        throw handleError(error, overrides);
      }
    },
  };
}

export function transformPaginated<TInput, TOutput>(
  paginatedData: PaginatedResponse<TInput>,
  transformer: (input: TInput) => TOutput
): PaginatedResponse<TOutput> {
  return {
    items: transformer(paginatedData.items),
    pagination: paginatedData.pagination,
  };
}
